import { createFileRoute, Link } from "@tanstack/react-router"
import { statusLabel, UNIT_STATUSES, type UnitStatus } from "@/inventory"
import { getDashboardCountsFn } from "@/inventory/rpc"
import { Button } from "@/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/ui/card"

export const Route = createFileRoute("/_inventory-layout/inventory")({
  loader: () => getDashboardCountsFn(),
  component: InventoryDashboard,
})

function InventoryDashboard() {
  const { total, byStatus, byType } = Route.useLoaderData()

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-semibold text-2xl tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground text-sm">
            Inventory overview by status and product type.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild>
            <Link to="/inventory/receive">Receive stock</Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/inventory/units">View units</Link>
          </Button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader>
            <CardDescription>Total units</CardDescription>
            <CardTitle className="text-3xl tabular-nums">{total}</CardTitle>
          </CardHeader>
        </Card>
        <KpiLinkCard
          search={{ type: "sim" }}
          description="SIMs"
          value={byType.sim}
        />
        <KpiLinkCard
          search={{ type: "device" }}
          description="Devices"
          value={byType.device}
        />
        <KpiLinkCard
          search={{ status: "available" }}
          description="Available"
          value={byStatus.available}
        />
      </div>

      <div>
        <h2 className="mb-3 font-medium text-lg">By status</h2>
        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {UNIT_STATUSES.map((status) => {
            const label = statusLabel(status)
            return (
              <Card key={status}>
                <CardHeader className="pb-2">
                  <CardDescription>{label}</CardDescription>
                  <CardTitle className="text-2xl tabular-nums">
                    {byStatus[status]}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Button variant="link" className="h-auto px-0" asChild>
                    <Link to="/inventory/units" search={{ status }}>
                      View {label.toLowerCase()}
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            )
          })}
        </div>
      </div>
    </div>
  )
}

interface KpiLinkCardProps {
  search: { type?: "sim" | "device"; status?: UnitStatus }
  description: string
  value: number
}

function KpiLinkCard({ search, description, value }: KpiLinkCardProps) {
  return (
    <Card className="transition-colors hover:bg-muted/40">
      <Link
        to="/inventory/units"
        search={search}
        className="block rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <CardHeader>
          <CardDescription>{description}</CardDescription>
          <CardTitle className="text-3xl tabular-nums">{value}</CardTitle>
        </CardHeader>
      </Link>
    </Card>
  )
}
